import { chunk, debounce } from "lodash";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import RenderIfVisible from "react-render-if-visible";
import useEvent from "react-use-event-hook";

import { useRtl } from "@ryde/redux";
import { IRideTableDto, Log } from "@ryde/shared";

import { KANBAN_CARD_HEIGHT, KANBAN_CARD_WIDTH } from "../../common";
import { CardBoardRow } from "./card-board-row";
import { GAP } from "./const";

const RESIZE_DEBOUNCE_MS = 50;
const RESIZE_MIN_DIFF = 30;

interface ICardBoardListProps {
  rides: IRideTableDto[];
}

export const CardBoardList: FC<ICardBoardListProps> = ({ rides }) => {
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { direction } = useRtl();

  const calculateColumnCount = useCallback((containerWidth: number) => {
    if (!containerWidth) {
      return 1;
    }

    return Math.max(1, Math.floor(containerWidth / (KANBAN_CARD_WIDTH + GAP)));
  }, []);

  const [columnCount, setColumnCount] = useState(1);

  const handleResize = useEvent(() => {
    const { height, width } = containerDimensions;

    const widthDiff = Math.abs(
      width - (containerRef.current?.offsetWidth ?? 0),
    );
    const heightDiff = Math.abs(
      height - (containerRef.current?.offsetHeight ?? 0),
    );

    Log.debug("CardBoardList size changed", widthDiff, heightDiff);

    if (widthDiff > RESIZE_MIN_DIFF || heightDiff > RESIZE_MIN_DIFF) {
      const newWidth = containerRef.current?.offsetWidth ?? 0;
      const newHeight = containerRef.current?.offsetHeight ?? 0;
      setContainerDimensions({
        width: newWidth,
        height: newHeight,
      });
      setColumnCount(calculateColumnCount(newWidth));
    }
  });

  const handleResizeDebounced = useMemo(
    () => debounce(handleResize, RESIZE_DEBOUNCE_MS),
    [handleResize],
  );

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const divElement = containerRef.current;
    const resizeObserver = new ResizeObserver(() => {
      // Render frequency bottleneck
      handleResizeDebounced();
    });

    resizeObserver.observe(divElement);

    handleResizeDebounced();
    return () => resizeObserver.disconnect();
  }, [handleResizeDebounced]);

  const ridesChunks = useMemo(() => {
    return chunk(rides, columnCount);
  }, [rides, columnCount]);

  const cardStyles: React.CSSProperties = {
    height: KANBAN_CARD_HEIGHT + GAP,
  };

  return (
    <div ref={containerRef} className="card-board-list">
      {ridesChunks.map((ridesChunk, index) => {
        return (
          <RenderIfVisible
            key={index}
            stayRendered={true}
            defaultHeight={KANBAN_CARD_HEIGHT + GAP}
          >
            <CardBoardRow
              key={index}
              index={index}
              style={cardStyles}
              rides={ridesChunk}
              columnCount={columnCount}
            />
          </RenderIfVisible>
        );
      })}
    </div>
  );
};
